import type { ReactNode } from 'react'

import type { AthenaRole, ProductKeyStatus } from '../../lib/api/types.ts'
import type { Tone } from './Metric.tsx'
import { StatusDot } from './StatusDot.tsx'

const TONE: Record<Tone, string> = {
  neutral: 'border-line text-fg-secondary',
  accent: 'border-accent text-accent',
  ok: 'border-ok text-ok',
  warn: 'border-warn text-warn',
  crit: 'border-crit text-crit',
}

/**
 * Rótulo de estado.
 *
 * Sempre com a palavra escrita: a cor é o segundo canal, nunca o único. Numa
 * tabela de licenças, "Revoked" em vermelho e "Suspended" em âmbar são a mesma
 * célula cinza para quem tem deuteranopia — a palavra é o que os separa.
 */
export function Badge({
  tone = 'neutral',
  dot = false,
  dashed = false,
  title,
  children,
}: {
  tone?: Tone
  dot?: boolean
  /** Borda tracejada: "registrado, mas não aconteceu" (simulação, intenção). */
  dashed?: boolean
  title?: string
  children: ReactNode
}) {
  return (
    <span
      title={title}
      className={`inline-flex shrink-0 items-center gap-1 border px-1.5 py-px text-micro uppercase tracking-label ${TONE[tone]} ${
        dashed ? 'border-dashed' : ''
      }`}
    >
      {dot && <StatusDot tone={tone} />}
      {children}
    </span>
  )
}

/**
 * Status da licença.
 *
 * `Created` é neutro e não âmbar de propósito: uma licença emitida e ainda não
 * ativada é o estado normal de quem provisiona antes de existir máquina, não um
 * problema. O único vermelho é `Revoked`, que é terminal.
 */
const STATUS_TONE: Record<ProductKeyStatus, Tone> = {
  Created: 'neutral',
  Active: 'ok',
  Suspended: 'warn',
  Revoked: 'crit',
  Expired: 'warn',
}

export const STATUS_LABEL: Record<ProductKeyStatus, string> = {
  Created: 'emitida',
  Active: 'ativa',
  Suspended: 'suspensa',
  Revoked: 'revogada',
  Expired: 'expirada',
}

const STATUS_TITLE: Record<ProductKeyStatus, string> = {
  Created: 'Emitida, ainda não ativada.',
  Active: 'Autoriza a ingestão.',
  Suspended: 'Ingestão cortada, reversível.',
  Revoked: 'Revogada em definitivo.',
  Expired: 'Vencida. Pode ser reativada.',
}

export function ProductKeyStatusBadge({ status }: { status: ProductKeyStatus }) {
  return (
    <Badge tone={STATUS_TONE[status]} dot title={STATUS_TITLE[status]}>
      {STATUS_LABEL[status]}
    </Badge>
  )
}

export const ROLE_LABEL: Record<AthenaRole, string> = {
  platform_admin: 'administrador',
  platform_staff: 'operador',
  tenant_admin: 'admin. da empresa',
  tenant_user: 'usuário da empresa',
}

const ROLE_TITLE: Record<AthenaRole, string> = {
  platform_admin: 'Empresas, máquinas, licenças, agentes, o painel ao vivo e o cadastro de usuários.',
  platform_staff: 'Empresas, máquinas, licenças, agentes e o painel ao vivo.',
  tenant_admin: 'Painel da própria empresa e o cadastro de usuários dela.',
  tenant_user: 'Painel da própria empresa, somente leitura.',
}

export function RoleBadge({ role }: { role: AthenaRole }) {
  return (
    <Badge tone={role === 'platform_admin' || role === 'tenant_admin' ? 'accent' : 'neutral'} title={ROLE_TITLE[role]}>
      {ROLE_LABEL[role]}
    </Badge>
  )
}

/**
 * Situação do registro.
 *
 * Remoção é lógica em toda a API, então "removida" é um estado exibível e não a
 * ausência da linha — e precisa se distinguir de "desativada", que é reversível.
 */
export function RecordStateBadge({
  isActive,
  deletedAt,
}: {
  isActive: boolean
  deletedAt: string | null
}) {
  if (deletedAt !== null) {
    return (
      <Badge tone="crit">removida</Badge>
    )
  }

  return isActive ? (
    <Badge tone="ok" dot>
      ativa
    </Badge>
  ) : (
    <Badge tone="warn" dot title="A ingestão de toda a frota desta empresa está cortada.">
      desativada
    </Badge>
  )
}
